/**
 * ZonePlayer — Independent mini-player for a single zone in a multi-zone layout.
 *
 * Each zone gets its own content loop: a filtered subset of the playlist (by
 * zonaId) that cycles independently. Scheduling/filtering is reused from the
 * existing scheduler infrastructure; this component only handles playback.
 *
 * Kept intentionally minimal compared to the main Stream scene — no debug overlay,
 * no interactive elements, no kiosk logic, no Canvas Bridge, no VideoPreloader.
 * Those features operate at the Container level (parent Stream scene) and don't
 * need per-zone duplication.
 */

import { FC, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import type { StreamMediaItem } from '~/stores/stream.store';
import {
  usePlayableUrl,
  applyMediaCrossOrigin,
  mediaCrossOriginProp,
} from '~/utils/hooks/usePlayableUrl';
import { useEffectiveFitMode } from '~/utils/hooks/useEffectiveFitMode';
import { VideoPlayer, ImagePlayer } from './styles';

interface ZonePlayerProps {
  /** Content items assigned to this zone (already filtered by zonaId). */
  playlist: StreamMediaItem[];
  /** Global loop duration shared across all zones so clocks stay in sync. */
  globalLoopDuration?: number;
  /** Disparado UMA vez no 1º paint real desta zona (img onLoad / vídeo onPlaying).
   *  O pai usa isso pra esconder a capa (freeze) assim que as zonas pintam, em vez
   *  de esperar o safety longo — mata a lentidão na ENTRADA de conteúdo multi-zona. */
  onReady?: () => void;
}

/**
 * A self-contained content player that loops through its own playlist.
 * Renders a single video or image at a time and auto-advances.
 */
const ZonePlayer: FC<ZonePlayerProps> = observer(
  ({ playlist: rawPlaylist, globalLoopDuration, onReady }) => {
    // MESMA REGRA DE ESCALA do conteúdo comum (2026-08-24): a mídia da zona
    // segue o effectiveFitMode (default stretch = preenche), igual ao viewport
    // único. Antes ficava no default 'cover' dos styled (regra divergente).
    const effectiveFitMode = useEffectiveFitMode();
    // Dispara onReady só 1× (no 1º paint desta zona). Ref (não state) → não re-renderiza.
    const readyFiredRef = useRef(false);
    const fireReady = useCallback(() => {
      if (readyFiredRef.current) return;
      readyFiredRef.current = true;
      onReady?.();
    }, [onReady]);
    // Each item gets inSeconds/outSeconds — same pattern as interactive elements
    const items = useMemo(() => {
      let seqOff = 0;
      return rawPlaylist.map((item) => {
        const inSec = item.startOffsetSeconds ?? seqOff;
        const dur = item.duration || 10;
        const outSec = inSec + dur;
        seqOff = outSec;
        return { ...item, inSeconds: inSec, outSeconds: outSec };
      });
    }, [rawPlaylist]);

    const localLoopDuration = useMemo(
      () =>
        items.length > 0 ? Math.max(...items.map((i) => i.outSeconds)) : 1,
      [items],
    );
    const loopDuration = globalLoopDuration ?? localLoopDuration;

    const [currentTime, setCurrentTime] = useState(0);
    const [playCount, setPlayCount] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamVideoRef = useRef<HTMLVideoElement>(null);
    const startTimeRef = useRef(performance.now());
    const prevActiveIdRef = useRef<string | null>(null);

    // Clock — ticks every 200ms, updates currentTime (same as interactiveElements.updateTime)
    useEffect(() => {
      if (items.length === 0) return;
      startTimeRef.current = performance.now();

      let lastLoggedItem = '';
      const interval = setInterval(() => {
        const elapsed =
          ((performance.now() - startTimeRef.current) / 1000) % loopDuration;
        const rounded = Math.round(elapsed * 10) / 10;
        const active = items.find(
          (i) => rounded >= i.inSeconds && rounded < i.outSeconds,
        );
        const activeName = active ? active.name : 'GAP';
        if (activeName !== lastLoggedItem) {
          lastLoggedItem = activeName;
          console.debug(
            `[ZonePlayer] t=${rounded.toFixed(1)}s → ${activeName} (in=${active?.inSeconds ?? '-'} out=${active?.outSeconds ?? '-'}) loop=${loopDuration}s`,
          );
        }
        setCurrentTime(rounded);
      }, 200);

      return () => clearInterval(interval);
    }, [items.length, loopDuration]);

    // Find active item — same logic as isInTimeWindow
    const activeItem = useMemo(
      () =>
        items.find(
          (i) => currentTime >= i.inSeconds && currentTime < i.outSeconds,
        ) ?? null,
      [items, currentTime],
    );

    // Track item changes for playCount (triggers stream reload)
    useEffect(() => {
      const newId = activeItem?.id ?? null;
      if (newId !== prevActiveIdRef.current) {
        prevActiveIdRef.current = newId;
        setPlayCount((c) => c + 1);
      }
    }, [activeItem?.id]);

    const currentItem = activeItem;
    const isGap = activeItem === null && items.length > 0;

    const playlistKey = useMemo(
      () => rawPlaylist.map((p) => p.id).join(','),
      [rawPlaylist],
    );
    useEffect(() => {
      startTimeRef.current = performance.now();
      setCurrentTime(0);
    }, [playlistKey]);

    // Resolve playable URL (cache or remote) — skip for streams (URL is already external)
    const skipUrlResolve = currentItem?.type === 'stream';
    const {
      url: playableUrl,
      source: playbackSource,
      isLoading: isUrlLoading,
    } = usePlayableUrl(skipUrlResolve ? null : currentItem, null);

    const advanceToNext = useCallback(() => {}, []);

    // Apply video source when current item changes
    useEffect(() => {
      const video = videoRef.current;
      if (!video || !currentItem || currentItem.type !== 'video') return;
      if (isUrlLoading || !playableUrl) return;

      applyMediaCrossOrigin(video, playbackSource);
      video.src = playableUrl;
      video.load();
      video.play().catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.warn('[ZonePlayer] Play failed:', err);
      });
    }, [currentItem, playableUrl, isUrlLoading, playbackSource]);

    // Image/stream timers removed — timeline clock handles item switching

    // Force stream video reload when item changes back to a non-YouTube stream
    useEffect(() => {
      if (!currentItem || currentItem.type !== 'stream') return;
      if (!currentItem.streamUrl) return;
      const video = streamVideoRef.current;
      if (!video) return;
      const url = currentItem.streamUrl;
      const bustUrl = url.includes('?')
        ? `${url}&_t=${Date.now()}`
        : `${url}?_t=${Date.now()}`;
      console.debug('[ZonePlayer] stream reload:', bustUrl);
      video.src = bustUrl;
      video.load();
      video
        .play()
        .catch((err) => console.warn('[ZonePlayer] play() rejected:', err));
    }, [playCount, currentItem]);

    const handleVideoEnded = useCallback(() => {
      advanceToNext();
    }, [advanceToNext]);

    const handleVideoError = useCallback(() => {
      console.warn('[ZonePlayer] Video error, advancing');
      fireReady(); // não segura a capa do pai se esta zona falhou
      advanceToNext();
    }, [advanceToNext, fireReady]);

    const handleImageError = useCallback(() => {
      console.warn('[ZonePlayer] Image error, advancing');
      fireReady(); // não segura a capa do pai se esta zona falhou
      advanceToNext();
    }, [advanceToNext, fireReady]);

    if (isGap || !currentItem) {
      return <div style={{ width: '100%', height: '100%' }} />;
    }

    // Igual ao viewport único (vide Stream/index.tsx): o fallback remoto vive
    // dentro do `usePlayableUrl`. Um `|| currentItem.url` aqui pintaria a
    // presigned CRUA antes da resolução — e ela rotaciona a cada sync, então
    // cada montagem seria uma URL inédita (fetch + cópia decodificada a mais no
    // renderer). Vide `.claude/rules/cache-offline-first.md`.
    const mediaUrl = playableUrl;
    const streamUrl = currentItem.streamUrl;
    const isStream = currentItem.type === 'stream' && !!streamUrl;

    const isYouTube = isStream && /(?:youtube\.com|youtu\.be)/.test(streamUrl);
    const shouldLoop = rawPlaylist.length === 1 && currentItem.type === 'video';

    if (isStream) {
      const isCurrentYouTube = isYouTube;
      const videoId = isYouTube
        ? streamUrl.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)?.[1]
        : null;

      return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
          {/* HLS/MP4 video — always mounted, paused when not active */}
          <VideoPlayer
            ref={streamVideoRef}
            playsInline
            loop
            muted
            controls={false}
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            $objectFit={effectiveFitMode}
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              inset: 0,
              opacity: isCurrentYouTube ? 0 : 1,
              pointerEvents: isCurrentYouTube ? 'none' : 'auto',
            }}
          />
          {/* YouTube iframe — only rendered when active */}
          {isCurrentYouTube && videoId && (
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playsinline=1`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                position: 'absolute',
                inset: 0,
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
      );
    }

    return (
      <>
        {currentItem.type === 'video' ? (
          <VideoPlayer
            ref={videoRef}
            autoPlay
            playsInline
            loop={shouldLoop}
            muted={false}
            controls={false}
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            crossOrigin={mediaCrossOriginProp(playbackSource)}
            $objectFit={effectiveFitMode}
            poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            onEnded={handleVideoEnded}
            onError={handleVideoError}
            onPlaying={fireReady}
            style={{
              transform: 'none',
              WebkitTransform: 'none',
              zoom: 1,
              touchAction: 'none',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          />
        ) : (
          !isUrlLoading &&
          mediaUrl && (
            <ImagePlayer
              src={mediaUrl}
              alt={currentItem.name}
              crossOrigin={mediaCrossOriginProp(playbackSource)}
              $objectFit={effectiveFitMode}
              onLoad={fireReady}
              onError={handleImageError}
            />
          )
        )}
      </>
    );
  },
);

export default ZonePlayer;
